import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  AdminPanelSettings
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { EasyCheckButton } from '../../common/Controls';
import { superAdminAuthService } from '../../../services/superAdmin/superAdminAuthService';

const LoginContainer = styled(Box)(() => ({
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)',
  position: 'relative'
}));

const LoginCard = styled(Box)(({ theme }) => ({
  backgroundColor: 'rgba(255, 255, 255, 0.95)',
  backdropFilter: 'blur(20px)',
  borderRadius: '24px',
  padding: theme.spacing(5),
  boxShadow: '0 20px 60px rgba(20, 184, 166, 0.2)',
  border: '1px solid rgba(20, 184, 166, 0.2)',
  maxWidth: '420px',
  width: '100%',
  margin: theme.spacing(2)
}));

const SuperAdminLogin = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await superAdminAuthService.login({ email, password });

      if (result.success) {
        navigate('/super-admin/verify-otp', {
          state: { email, password }
        });
      } else {
        setError(result.message || 'Invalid credentials.');
      }
    } catch {
      setError('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <LoginContainer>
      <LoginCard>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <AdminPanelSettings sx={{ fontSize: 48, color: '#14b8a6', mb: 1 }} />
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#0f172a' }}>
            <span style={{ color: '#0f172a' }}>Easy</span>
            <span style={{ color: '#14b8a6' }}>Check</span>
          </Typography>
          <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5 }}>
            Admin Portal
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            sx={{ mb: 2 }}
            InputProps={{ sx: { borderRadius: 2 } }}
          />
          <TextField
            fullWidth
            label="Password"
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            sx={{ mb: 3 }}
            InputProps={{
              sx: { borderRadius: 2 },
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
          <EasyCheckButton
            type="submit"
            fullWidth
            disabled={loading}
            sx={{
              py: 1.5,
              borderRadius: 2,
              backgroundColor: '#14b8a6',
              '&:hover': { backgroundColor: '#0d9488' },
              fontWeight: 600,
              fontSize: '1rem'
            }}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : 'Sign In'}
          </EasyCheckButton>
        </form>
      </LoginCard>
    </LoginContainer>
  );
};

export default SuperAdminLogin;
